import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from 'react'

import { cn } from '@/lib/cn'
import { Spinner } from '@/components/ui/Spinner'

type Variant = 'filled' | 'outlined' | 'text' | 'danger' | 'ghost'
type Size = 'sm' | 'md'

/// Вариант `outlined` остался под своим именем, но обводки у него больше нет:
/// вторичная кнопка держится заливкой утопленного тона. Переименовывать его в
/// `tonal` значило бы править полсотни мест ради синонима.
const VARIANTS: Record<Variant, string> = {
  filled: 'bg-brand text-white shadow-brand hover:bg-brand-dark disabled:bg-ink-disabled disabled:shadow-none',
  outlined:
    'bg-surface-alt text-ink hover:bg-surface-sunken disabled:bg-surface-alt disabled:text-ink-disabled',
  text: 'text-brand hover:bg-brand-tint disabled:text-ink-disabled',
  danger: 'bg-danger text-white hover:brightness-95 disabled:bg-ink-disabled',
  ghost: 'text-ink-muted hover:bg-surface-alt hover:text-ink disabled:text-ink-disabled',
}

const SIZES: Record<Size, string> = {
  sm: 'h-9 gap-1.5 px-3 text-[13px]',
  md: 'h-11 gap-2 px-5 text-sm',
}

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant
  size?: Size
  loading?: boolean
  icon?: ReactNode
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { variant = 'filled', size = 'md', loading = false, icon, className, children, disabled, ...rest },
  ref,
) {
  return (
    <button
      ref={ref}
      type="button"
      disabled={disabled || loading}
      className={cn(
        'inline-flex shrink-0 items-center justify-center rounded-field font-semibold whitespace-nowrap',
        // Нажатие проседает на 2%: единственная обратная связь, которую видно
        // раньше, чем ответит сервер.
        'transition duration-150 ease-out-soft active:scale-[0.98]',
        'disabled:cursor-not-allowed disabled:active:scale-100',
        VARIANTS[variant],
        SIZES[size],
        className,
      )}
      {...rest}
    >
      {loading ? <Spinner size={size === 'sm' ? 14 : 18} /> : icon}
      {children}
    </button>
  )
})

/// Квадратная кнопка под одну иконку (IconButton в Material).
export const IconButton = forwardRef<
  HTMLButtonElement,
  ButtonHTMLAttributes<HTMLButtonElement> & { label: string; children: ReactNode }
>(function IconButton({ label, className, children, ...rest }, ref) {
  return (
    <button
      ref={ref}
      type="button"
      aria-label={label}
      title={label}
      className={cn(
        'inline-flex size-9 shrink-0 items-center justify-center rounded-xl text-ink-muted',
        'transition duration-150 ease-out-soft hover:bg-surface-alt hover:text-ink active:scale-95',
        'disabled:cursor-not-allowed disabled:text-ink-disabled disabled:hover:bg-transparent disabled:active:scale-100',
        className,
      )}
      {...rest}
    >
      {children}
    </button>
  )
})
